export type AppLanguage = "en" | "de" | "fr" | "it" | "es";

export const MANUAL_LANGUAGE_KEY = "i18nextManualLng";
export const GEO_LANGUAGE_KEY = "i18nextGeoLng";
export const GEO_LANGUAGE_TS_KEY = "i18nextGeoLngTs";
export const GEO_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const GEO_LOOKUP_URL = "/api/geo";
const GERMAN_SPEAKING_COUNTRIES = new Set(["DE", "AT"]);
const FRENCH_SPEAKING_COUNTRIES = new Set(["FR", "LU", "MC"]);
const ITALIAN_SPEAKING_COUNTRIES = new Set(["IT", "SM", "VA"]);
const SPANISH_SPEAKING_COUNTRIES = new Set([
  "ES",
  "AR",
  "BO",
  "CL",
  "CO",
  "CR",
  "CU",
  "DO",
  "EC",
  "GQ",
  "GT",
  "HN",
  "MX",
  "NI",
  "PA",
  "PE",
  "PR",
  "PY",
  "SV",
  "UY",
  "VE",
]);
const BELGIUM_FRENCH_REGIONS = new Set(["BRU", "WAL", "WBR", "WHT", "WLG", "WLX", "WNA"]);
const BELGIUM_DUTCH_REGIONS = new Set(["VLG", "VAN", "VBR", "VLI", "VOV", "VWV"]);
const SWISS_GERMAN_CANTONS = new Set([
  "AG",
  "AI",
  "AR",
  "BL",
  "BS",
  "GL",
  "LU",
  "NW",
  "OW",
  "SG",
  "SH",
  "SO",
  "SZ",
  "TG",
  "UR",
  "ZG",
  "ZH",
]);
const SWISS_ITALIAN_CANTONS = new Set(["TI"]);
const SWISS_FRENCH_CANTONS = new Set(["FR", "GE", "JU", "NE", "VD", "VS"]);
const SUPPORTED_LANGUAGES = new Set<AppLanguage>(["en", "de", "fr", "it", "es"]);

type FetchLike = typeof fetch;

interface ResolveInitialLanguageOptions {
  fetchImpl?: FetchLike;
  now?: number;
  pathname?: string;
}

function getStorage() {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function normalizeLanguage(value: string | null | undefined): AppLanguage | undefined {
  if (!value) {
    return undefined;
  }

  const normalized = value.toLowerCase().split("-")[0];
  return SUPPORTED_LANGUAGES.has(normalized as AppLanguage)
    ? (normalized as AppLanguage)
    : undefined;
}

function readStorageLanguage(key: string): AppLanguage | undefined {
  return normalizeLanguage(getStorage()?.getItem(key));
}

function readStorageTimestamp(key: string): number | undefined {
  const raw = getStorage()?.getItem(key);
  if (!raw) {
    return undefined;
  }

  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function writeStorageValue(key: string, value: string) {
  try {
    getStorage()?.setItem(key, value);
  } catch {
    // Ignore localStorage failures (private mode, quota, etc.)
  }
}

function currentPathname() {
  if (typeof window === "undefined") {
    return "/";
  }

  return window.location.pathname;
}

function createTimeoutControl(timeoutMs: number) {
  if (typeof AbortSignal !== "undefined" && "timeout" in AbortSignal) {
    return {
      signal: AbortSignal.timeout(timeoutMs),
      cancel: undefined,
    };
  }

  if (typeof AbortController !== "undefined") {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    return {
      signal: controller.signal,
      cancel: () => clearTimeout(timeoutId),
    };
  }

  return {
    signal: undefined,
    cancel: undefined,
  };
}

export function resolveLanguageFromPath(pathname = currentPathname()): AppLanguage | undefined {
  const pathLang = pathname.split("/").filter(Boolean)[0];
  return normalizeLanguage(pathLang);
}

export function readManualLanguage(): AppLanguage | undefined {
  return readStorageLanguage(MANUAL_LANGUAGE_KEY);
}

export function readFreshGeoLanguage(now = Date.now()): AppLanguage | undefined {
  const language = readStorageLanguage(GEO_LANGUAGE_KEY);
  const timestamp = readStorageTimestamp(GEO_LANGUAGE_TS_KEY);

  if (!language || timestamp === undefined) {
    return undefined;
  }

  return now - timestamp < GEO_TTL_MS ? language : undefined;
}

export function cacheGeoLanguage(language: AppLanguage, now = Date.now()) {
  writeStorageValue(GEO_LANGUAGE_KEY, language);
  writeStorageValue(GEO_LANGUAGE_TS_KEY, String(now));
}

export function persistManualLanguage(language: AppLanguage) {
  writeStorageValue(MANUAL_LANGUAGE_KEY, language);
  writeStorageValue("i18nextLng", language);
}

export async function fetchGeoLanguage(fetchImpl?: FetchLike): Promise<AppLanguage | undefined> {
  const fetchFn = fetchImpl ?? (typeof fetch === "function" ? fetch : undefined);
  if (typeof fetchFn !== "function") {
    return undefined;
  }

  const { signal, cancel } = createTimeoutControl(3000);

  try {
    const response = await fetchFn(GEO_LOOKUP_URL, {
      headers: {
        accept: "application/json",
      },
      signal,
    });

    if (!response.ok) {
      return undefined;
    }

    const data = await response.json();
    if (!data || typeof data !== "object") {
      return undefined;
    }

    const cc = data?.country_code;
    const rawRegionCode = typeof data?.region_code === "string" ? data.region_code.toUpperCase() : undefined;
    const regionCode = rawRegionCode?.replace(/^(BE|CH)-/, "");

    if (cc === "BE" && regionCode) {
      if (BELGIUM_FRENCH_REGIONS.has(regionCode)) return "fr";
      if (BELGIUM_DUTCH_REGIONS.has(regionCode)) return "en";
    }

    if (cc === "CH" && regionCode) {
      if (SWISS_GERMAN_CANTONS.has(regionCode)) return "de";
      if (SWISS_ITALIAN_CANTONS.has(regionCode)) return "it";
      if (SWISS_FRENCH_CANTONS.has(regionCode)) return "fr";
      return "en";
    }

    if (GERMAN_SPEAKING_COUNTRIES.has(cc)) return "de";
    if (FRENCH_SPEAKING_COUNTRIES.has(cc)) return "fr";
    if (ITALIAN_SPEAKING_COUNTRIES.has(cc)) return "it";
    if (SPANISH_SPEAKING_COUNTRIES.has(cc)) return "es";
    return undefined;
  } catch {
    return undefined;
  } finally {
    cancel?.();
  }
}

export async function resolveInitialLanguage({
  fetchImpl,
  now = Date.now(),
  pathname = currentPathname(),
}: ResolveInitialLanguageOptions = {}): Promise<AppLanguage> {
  const pathLanguage = resolveLanguageFromPath(pathname);
  if (pathLanguage) {
    return pathLanguage;
  }

  const manualLanguage = readManualLanguage();
  if (manualLanguage) {
    return manualLanguage;
  }

  const cachedGeoLanguage = readFreshGeoLanguage(now);
  if (cachedGeoLanguage) {
    return cachedGeoLanguage;
  }

  const geoLanguage = await fetchGeoLanguage(fetchImpl);
  if (geoLanguage) {
    cacheGeoLanguage(geoLanguage, now);
    return geoLanguage;
  }

  return "en";
}
